import { apiClient } from './client';
import { PaginatedResponse } from '../types/pagination';
import {
  GenerateNotificationsSummary,
  Notification,
  NotificationCategory,
  NotificationDestination,
  NotificationSummary,
} from '../types/notification';

export interface ListNotificationsParams {
  page: number;
  pageSize: number;
  unreadOnly?: boolean;
  category?: NotificationCategory;
}

export const listNotifications = async (
  params: ListNotificationsParams,
): Promise<PaginatedResponse<Notification>> => {
  const { data } = await apiClient.get('/notifications', { params });
  // A conditionally-cached GET (identical URL/params, e.g. this hook's own
  // 8s poll) can come back as a bodyless 304 that the browser didn't
  // transparently splice a cached body into, leaving `data` empty/
  // malformed rather than the expected paginated envelope. Silently
  // treating that as "genuinely zero notifications" would wipe out
  // already-loaded data for no reason; throwing instead makes React Query
  // treat it like any other failed background refetch and keep the last
  // good result.
  if (!data || !Array.isArray(data.data) || !data.meta) {
    throw new Error('Received an invalid notifications response');
  }
  return data;
};

export const getUnreadNotificationCount = async (): Promise<number> => {
  const { data } = await apiClient.get('/notifications/unread-count');
  if (typeof data?.data?.count !== 'number') {
    throw new Error('Received an invalid unread-count response');
  }
  return data.data.count;
};

export const getNotificationSummary = async (): Promise<NotificationSummary> => {
  const { data } = await apiClient.get('/notifications/summary');
  return data.data;
};

export const markNotificationRead = async (id: number): Promise<Notification> => {
  const { data } = await apiClient.post(`/notifications/${id}/read`);
  return data.data;
};

export const markAllNotificationsRead = async (): Promise<void> => {
  await apiClient.post('/notifications/read-all');
};

export const generateNotifications = async (): Promise<GenerateNotificationsSummary> => {
  const { data } = await apiClient.post('/notifications/generate');
  return data.data;
};

export const getNotificationDestination = async (id: number): Promise<NotificationDestination> => {
  const { data } = await apiClient.get(`/notifications/${id}/destination`);
  return data.data;
};
